import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FileText, Plane, PiggyBank, DollarSign, History, Receipt, Route as RouteIcon,
  Search, ExternalLink, Paperclip,
} from 'lucide-react';

const TABS = [
  ['payslips', 'Payslips', FileText],
  ['leave_balances', 'Leave Balances', Plane],
  ['superannuation', 'Superannuation', PiggyBank],
  ['reimbursements', 'Reimbursements', DollarSign],
  ['payment_history', 'Payment History', History],
];

const STATUS_STYLE = {
  DRAFT: 'bg-muted text-muted-foreground border-border',
  SUBMITTED: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-blue-50 text-blue-700 border-blue-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  CANCELLED: 'bg-muted text-muted-foreground border-border',
  PAID: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function StatusBadge({ item }) {
  let label;
  if (item.claim_type === 'MILEAGE') {
    // Trip-log workflow status — never a payment/claim status.
    label = item.status === 'APPROVED' ? 'Trip Approved'
      : item.status === 'SUBMITTED' ? 'Trip Submitted'
      : item.status === 'REJECTED' ? 'Trip Rejected'
      : (item.status || '').charAt(0) + (item.status || '').slice(1).toLowerCase();
  } else {
    label = item.status === 'APPROVED' && item.paid_state_authoritative
      ? 'Approved — Awaiting Payment'
      : item.status === 'PAID' ? 'Paid' : (item.status || '').charAt(0) + (item.status || '').slice(1).toLowerCase();
  }
  return (
    <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[item.status] || ''}`}
           data-testid={`reimb-status-${item.id}`}>{label}</Badge>
  );
}

function UnavailableTab({ tabKey, message }) {
  return (
    <div className="py-14 text-center max-w-md mx-auto space-y-2" data-testid={`payroll-unavailable-${tabKey}`}>
      <div className="text-sm font-medium text-muted-foreground">Not available</div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function DetailDialog({ refId, onClose }) {
  const [state, setState] = useState({ data: null, timeline: [], loading: true });
  useEffect(() => {
    if (!refId) return;
    const [ct, id] = refId.split(':');
    api.get(`/staff/me/payroll/reimbursements/${ct}/${id}`)
      .then((r) => setState({ data: r.data.data, timeline: r.data.timeline, loading: false }))
      .catch(() => { toast.error('Unable to load claim details'); onClose(); });
  }, [refId, onClose]);

  const openReceipt = async (d) => {
    try {
      const r = await api.get(d.receipt_endpoint.replace('/api', ''), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Unable to open the receipt'); }
  };

  const d = state.data;
  return (
    <Dialog open={!!refId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reimb-detail-dialog">
        <DialogHeader><DialogTitle className="text-base">Reimbursement claim</DialogTitle></DialogHeader>
        {state.loading || !d ? <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div> : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium" data-testid="reimb-detail-description">{d.description}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {d.claim_type === 'MILEAGE' ? `Mileage · ${d.distance_km} km` : `Expense · ${d.category_code}`} · {d.claim_date || '—'}
                </div>
              </div>
              <StatusBadge item={d} />
            </div>
            <div className="text-lg font-semibold" data-testid="reimb-detail-amount">
              {d.currency} {Number(d.amount).toFixed(2)}
              {d.amount_is_estimate && <span className="ml-2 text-[10px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 align-middle">Estimate</span>}
            </div>
            {d.payment_status === 'PAID' && (
              <div className="text-xs text-emerald-700" data-testid="reimb-detail-paid">Paid{d.payment_date ? ` on ${d.payment_date}` : ''}</div>
            )}
            {d.payment_status === 'AWAITING_PAYMENT' && (
              <div className="text-xs text-blue-700" data-testid="reimb-detail-awaiting">Approved — awaiting payment by finance</div>
            )}
            {d.payment_status === 'UNAVAILABLE' && (
              <div className="text-xs text-muted-foreground" data-testid="reimb-detail-payment-unavailable">{d.payment_note || 'Payment status unavailable.'}</div>
            )}
            {d.rejection_reason && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded p-2" data-testid="reimb-detail-rejection">Reason: {d.rejection_reason}</div>
            )}
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1.5">Status timeline</div>
              <div className="space-y-1.5" data-testid="reimb-detail-timeline">
                {state.timeline.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                    <span className="font-medium">{t.event}</span>
                    <span className="text-muted-foreground">{t.at ? new Date(t.at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
            {d.has_receipt && d.receipt_endpoint && (
              <Button size="sm" variant="outline" onClick={() => openReceipt(d)} data-testid="reimb-detail-receipt-btn">
                <Paperclip className="h-3.5 w-3.5 mr-1.5" />View receipt
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReimbursementsTab() {
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', claim_type: '', q: '', date_from: '', date_to: '' });
  const [detailRef, setDetailRef] = useState(null);
  const pageSize = 20;

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get('/staff/me/payroll/reimbursements', { params })
      .then((r) => { setItems(r.data.data); setSummary(r.data.summary); setTotal(r.data.total); })
      .catch(() => setItems([]));
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };

  return (
    <div className="space-y-4" data-testid="payroll-tab-reimbursements">
      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['Total claims', summary.total, 'reimb-summary-total'],
              ['Submitted', summary.submitted, 'reimb-summary-submitted'],
              ['Awaiting payment', summary.awaiting_payment, 'reimb-summary-awaiting'],
              ['Paid', summary.paid, 'reimb-summary-paid']].map(([label, value, tid]) => (
              <div key={tid} className="rounded-lg border bg-white px-3 py-2.5" data-testid={tid}>
                <div className="text-lg font-semibold leading-none">{value ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
              </div>
            ))}
          </div>
          {summary.mileage_estimates?.count > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800"
                 data-testid="reimb-mileage-estimates-note">
              {summary.mileage_estimates.count} mileage trip{summary.mileage_estimates.count > 1 ? 's' : ''} · est. AUD {Number(summary.mileage_estimates.estimated_total).toFixed(2)} — estimates only, not reimbursement claims. Payment status unavailable.
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-8 pl-8 text-xs" placeholder="Search claims…" value={filters.q}
                 onChange={(e) => setF('q', e.target.value)} data-testid="reimb-filter-search" />
        </div>
        <select className="h-8 rounded-md border border-input bg-white px-2 text-xs" value={filters.claim_type}
                onChange={(e) => setF('claim_type', e.target.value)} data-testid="reimb-filter-type" aria-label="Claim type">
          <option value="">All types</option>
          <option value="EXPENSE">Expenses</option>
          <option value="MILEAGE">Mileage</option>
        </select>
        <select className="h-8 rounded-md border border-input bg-white px-2 text-xs" value={filters.status}
                onChange={(e) => setF('status', e.target.value)} data-testid="reimb-filter-status" aria-label="Status">
          <option value="">All statuses</option>
          {['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'CANCELLED', 'PAID'].map((s) => (
            <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
          ))}
        </select>
        <Input type="date" className="h-8 w-[140px] text-xs" value={filters.date_from}
               onChange={(e) => setF('date_from', e.target.value)} data-testid="reimb-filter-from" aria-label="From date" />
        <Input type="date" className="h-8 w-[140px] text-xs" value={filters.date_to}
               onChange={(e) => setF('date_to', e.target.value)} data-testid="reimb-filter-to" aria-label="To date" />
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <Link to="/my/expenses" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="reimb-link-expenses">
          <Receipt className="h-3.5 w-3.5" />Submit an expense claim<ExternalLink className="h-3 w-3" />
        </Link>
        <Link to="/my/trip-logs" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="reimb-link-trips">
          <RouteIcon className="h-3.5 w-3.5" />Log a trip / mileage<ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {items === null ? (
        <div className="py-10 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="reimb-empty">
          No reimbursement claims found. Use the links above to submit an expense or log a trip.
        </div>
      ) : (
        <div className="space-y-2" data-testid="reimb-list">
          {items.map((i) => (
            <button key={i.ref} onClick={() => setDetailRef(i.ref)}
                    className="w-full text-left rounded-lg border bg-white px-3.5 py-3 hover:border-primary/40 transition-colors flex flex-wrap items-center gap-2"
                    data-testid={`reimb-item-${i.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-medium truncate">{i.description}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {i.claim_type === 'MILEAGE' ? `Mileage · ${i.distance_km} km` : `Expense · ${i.category_code}`} · {i.claim_date || '—'}
                </div>
              </div>
              <div className="text-sm font-semibold whitespace-nowrap">
                {i.currency} {Number(i.amount).toFixed(2)}
                {i.amount_is_estimate && <span className="ml-1 text-[9px] font-normal text-amber-700">est.</span>}
              </div>
              <StatusBadge item={i} />
            </button>
          ))}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="reimb-pagination-info">Page {page} of {Math.ceil(total / pageSize)} · {total} claims</span>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7" disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)} data-testid="reimb-page-prev">Previous</Button>
            <Button size="sm" variant="outline" className="h-7" disabled={page >= Math.ceil(total / pageSize)}
                    onClick={() => setPage((p) => p + 1)} data-testid="reimb-page-next">Next</Button>
          </div>
        </div>
      )}

      {detailRef && <DetailDialog refId={detailRef} onClose={() => setDetailRef(null)} />}
    </div>
  );
}

export default function PayrollPage() {
  const [tab, setTab] = useState('reimbursements');
  const [state, setState] = useState({ tabs: null, error: null, loading: true });

  useEffect(() => {
    api.get('/staff/me/payroll/summary')
      .then((r) => setState({ tabs: r.data.data.tabs, error: null, loading: false }))
      .catch((e) => setState({
        tabs: null, loading: false,
        error: e?.response?.status === 404
          ? 'Payroll self-service is not enabled for your organisation yet.'
          : 'Unable to load the Payroll area.',
      }));
  }, []);

  if (state.loading) return <div className="py-12 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>;
  if (state.error) return <div className="py-12 text-center text-sm text-muted-foreground" data-testid="payroll-unavailable">{state.error}</div>;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="staff-payroll-page">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Payroll sections">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  data-testid={`payroll-tab-btn-${key}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${tab === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-muted-foreground border-border hover:border-primary/40'}`}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>
      {tab === 'reimbursements'
        ? <ReimbursementsTab />
        : <UnavailableTab tabKey={tab} message={state.tabs[tab]?.message} />}
    </div>
  );
}
